import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Loader2, History, ChevronRight, Ban, Eye } from 'lucide-react'
import { useLineCampaignHistory, useLineCampaignRecipients, useLineCampaignVisits } from './useLineCampaign'
import { usePageTranslation } from '@/i18n/useTranslation'
import { lineCampaignTranslations as enT } from '@/i18n/locales/en/lineCampaign'
import { lineCampaignTranslations as thT } from '@/i18n/locales/th/lineCampaign'

type T = ReturnType<typeof usePageTranslation>

export function formatSentAt(s: string | null): string {
  if (!s) return '—'
  const d = new Date(s.replace(' ', 'T'))
  if (isNaN(d.getTime())) return s
  const date = d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  return `${date}, ${time}`
}

/**
 * Visit attribution strip for ONE expanded history row — how many recipients
 * visited the store after the send (via the campaign's utm-tagged links). Lazy:
 * only fetched when the row is expanded. Handles every state honestly:
 *   loading · unavailable (PostHog down, visits===-1) · none yet (0, older/no-link
 *   sends) · a real figure. Never claims data for sends that carry no tag.
 */
export function CampaignVisits({ logName, t }: { logName: string; t: T }) {
  const { data, isLoading } = useLineCampaignVisits(logName, true)

  const base = 'flex items-center gap-2 px-4 py-2.5 text-xs border-b border-gray-100'

  if (isLoading) {
    return (
      <div className={`${base} text-muted-foreground`}>
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        {t.visitsLoading}
      </div>
    )
  }
  if (!data || data.visits === -1) {
    return <div className={`${base} text-gray-400`}>{t.visitsUnavailable}</div>
  }
  if (data.visits === 0) {
    return (
      <div className={`${base} text-gray-400 flex-col items-start gap-0.5`}>
        <span>{t.visitsNone}</span>
        <span className="text-[11px] text-gray-400">{t.visitsNote}</span>
      </div>
    )
  }

  const unique = data.unique_visitors > 0
    ? ` (${data.unique_visitors.toLocaleString('en-US')} ${t.visitsUniqueSuffix})`
    : ''

  return (
    <div className={`${base} text-gray-700`}>
      <Eye className="w-3.5 h-3.5 text-gray-500" />
      <span className="font-medium text-gray-900">
        {t.visitsLabel} {data.visits.toLocaleString('en-US')}
      </span>
      {unique && <span className="text-gray-500">{unique}</span>}
      {data.orders > 0 && (
        <Badge className="bg-blue-100 text-blue-700 border-0 ml-auto">
          {t.visitsOrders.replace('{n}', data.orders.toLocaleString('en-US'))}
        </Badge>
      )}
    </div>
  )
}

/**
 * Recipient list for ONE expanded history row. Its own component so the query
 * is only mounted (and only fires) once the row is expanded — lazy per-row.
 */
const RECIPIENTS_PAGE_SIZE = 25

export function RecipientList({ logName, t }: { logName: string; t: T }) {
  const { data, isLoading, isError } = useLineCampaignRecipients(logName, true)
  const [page, setPage] = useState(0)

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-4 py-4 text-xs text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        {t.recipientsLoading}
      </div>
    )
  }
  if (isError) {
    return <p className="px-4 py-4 text-xs text-red-600">{t.recipientsError}</p>
  }
  // recorded=false → old send with no stored ids; or an empty list.
  if (!data?.recorded || data.recipients.length === 0) {
    return <p className="px-4 py-4 text-xs text-muted-foreground">{t.recipientsEmpty}</p>
  }

  const cappedNote =
    data.capped && data.checked > 0
      ? t.recipientsCappedNote
          .replace('{n}', data.checked.toLocaleString('en-US'))
          .replace('{total}', data.total.toLocaleString('en-US'))
      : null

  // Paginate so we never dump the whole (potentially 1,000+) recipient list.
  const total = data.recipients.length
  const pageCount = Math.max(1, Math.ceil(total / RECIPIENTS_PAGE_SIZE))
  const safePage = Math.min(page, pageCount - 1)
  const start = safePage * RECIPIENTS_PAGE_SIZE
  const pageRows = data.recipients.slice(start, start + RECIPIENTS_PAGE_SIZE)

  return (
    <div className="bg-gray-50/60">
      <ul className="py-1">
        {pageRows.map((r) => (
          <li
            key={r.line_user_id}
            className="flex items-center gap-3 px-4 py-1.5 text-sm"
          >
            <span className="flex-1 min-w-0 truncate text-[#111]">{r.name}</span>
            {r.blocked === true ? (
              <Badge className="bg-red-100 text-red-700 border-0 gap-1">
                <Ban className="w-3 h-3" />
                {t.recipientsBlocked}
              </Badge>
            ) : r.blocked === false ? (
              <span className="text-xs text-gray-400">{t.recipientsReached}</span>
            ) : null}
          </li>
        ))}
      </ul>
      {pageCount > 1 && (
        <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-gray-100">
          <span className="text-[11px] text-gray-400">
            {start + 1}–{Math.min(start + RECIPIENTS_PAGE_SIZE, total)} / {total.toLocaleString('en-US')}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
              className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 disabled:opacity-40 hover:bg-gray-50"
            >
              ‹
            </button>
            <span className="text-[11px] text-gray-500 tabular-nums px-1">
              {safePage + 1}/{pageCount}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={safePage >= pageCount - 1}
              className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 disabled:opacity-40 hover:bg-gray-50"
            >
              ›
            </button>
          </div>
        </div>
      )}
      {cappedNote && (
        <p className="px-4 py-2 text-[11px] text-gray-400 border-t border-gray-100">{cappedNote}</p>
      )}
    </div>
  )
}

/**
 * Past TARGETED campaign sends — reused on BOTH the Campaigns page (full card,
 * variant="card") and the LINE OA overview (embedded, variant="plain").
 * Delivered = recipient_count (the honest count LINE accepted). Opens/clicks
 * aren't measurable for multicast — omitted on purpose.
 *
 * Each row is EXPANDABLE: expanding it lazily fetches WHO the send reached
 * (names) and whether each recipient has blocked the OA.
 *
 * - `limit` caps the visible rows (overview shows the recent N).
 * - `variant="plain"` drops the outer Card + title so the caller can wrap it in
 *   its own section (the overview groups it under a shared heading).
 */
export function CampaignSentHistory({
  enabled,
  limit,
  variant = 'card',
}: {
  enabled: boolean
  limit?: number
  variant?: 'card' | 'plain'
}) {
  const t = usePageTranslation({ en: enT, th: thT })
  const navigate = useNavigate()
  const { data, isLoading } = useLineCampaignHistory(enabled)
  const all = data?.history ?? []
  const rows = limit != null ? all.slice(0, limit) : all

  const body = isLoading ? (
    <div className="flex items-center justify-center py-8">
      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
    </div>
  ) : rows.length === 0 ? (
    <p className="text-sm text-muted-foreground py-6 text-center">{t.historyEmpty}</p>
  ) : (
    <div className="border border-gray-100 rounded-lg overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 border-b border-gray-100">
        <span className="w-4 shrink-0" aria-hidden />
        <span className="text-xs font-medium text-gray-600 w-36 shrink-0">{t.historyColDate}</span>
        <span className="text-xs font-medium text-gray-600 flex-1">{t.historyColMessage}</span>
        <span className="text-xs font-medium text-gray-600 w-24 text-right shrink-0">{t.historyColDelivered}</span>
        <span className="text-xs font-medium text-gray-600 w-20 text-right shrink-0">{t.historyColStatus}</span>
      </div>
      <ScrollArea className="max-h-[340px]">
        {rows.map((r) => (
            <div key={r.name} className="border-b border-gray-50 last:border-0">
              <button
                type="button"
                onClick={() => navigate(`/line-oa/campaigns/sent/${encodeURIComponent(r.name)}`)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50/70 transition-colors"
              >
                <ChevronRight className="w-4 h-4 shrink-0 text-gray-400" />
                <span className="text-xs text-gray-600 w-36 shrink-0">{formatSentAt(r.sent_at)}</span>
                <span className="text-sm text-[#111] flex-1 min-w-0 truncate">
                  {r.content_preview || <span className="text-gray-400">—</span>}
                </span>
                <span className="text-sm text-gray-900 w-24 text-right shrink-0">
                  {r.recipient_count.toLocaleString('en-US')} <span className="text-gray-500">{t.historyPeople}</span>
                </span>
                <span className="w-20 text-right shrink-0">
                  {r.status === 'sent' ? (
                    <Badge className="bg-green-100 text-green-700 border-0">{t.historyStatusSent}</Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-700 border-0">{t.historyStatusFailed}</Badge>
                  )}
                </span>
              </button>
            </div>
        ))}
      </ScrollArea>
    </div>
  )

  if (variant === 'plain') return body

  return (
    <Card className="rounded-[8px] border border-black/[0.1]">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-gray-500" />
          <span className="font-medium">{t.historyTitle}</span>
        </div>
        {body}
      </CardContent>
    </Card>
  )
}
